import { EnrichedActivityLogCategoryEnum } from '@/types/Api';
import type { Employee, EnrichedActivityLog } from '@/types/Api';
import type { PreventionAssignment } from '@/types/Api';
import { formatDateToDe } from '@/utils/helpers';
import { printEntityName } from '@/utils/utils';

export const OPTIMISTIC_ACTIVITY_LOG_TTL_MS = 30000;

export type FirstAidOptimisticActionType = 'created' | 'updated' | 'assigned' | 'deleted';
export type MhOptimisticActionType = 'created' | 'note_updated' | 'closed';
export type MaternityNotificationOptimisticActionType = 'created' | 'updated';
export type PreventionOptimisticActionType =
  | 'deadline_updated'
  | 'completion_created'
  | 'completion_updated'
  | 'receipt_uploaded'
  | 'receipt_deleted';
export type InstructionAssignmentOptimisticActionType = 'due_date_updated';

export type InstructionAssignmentOptimisticTranslationParams = {
  employeeName: string;
  topicName: string;
  oldDueDate: string;
  newDueDate: string;
};

export const createOptimisticFirstAidLogPayload = ({
  employeeId,
  employees,
  actionType,
  actorName,
  incidentDateTime,
  fallbackEmployeeName,
}: {
  employeeId?: string;
  employees: Employee[];
  actionType: FirstAidOptimisticActionType;
  actorName: string;
  incidentDateTime: string;
  fallbackEmployeeName?: string;
}): { employeeId: string; entry: EnrichedActivityLog } | null => {
  if (!employeeId) return null;
  if (!incidentDateTime) return null;

  const selectedEmployee = employees.find((employee) => employee.id === employeeId);
  const employeeName =
    (selectedEmployee ? printEntityName(selectedEmployee) : '') || fallbackEmployeeName || '';
  const incidentDate = formatDateToDe(incidentDateTime);

  if (!employeeName || !incidentDate) return null;

  return {
    employeeId,
    entry: {
      id: `optimistic-${actionType}-${Date.now()}`,
      timestamp: new Date().toISOString(),
      translationKeyLabel: `activity_log.first_aid_log.${actionType}.label`,
      translationKeyTitle: `activity_log.first_aid_log.${actionType}.title`,
      translationParams:
        actionType === 'created'
          ? {
              submitterName: actorName,
              incidentDate,
              affectedEmployeeName: employeeName,
            }
          : actionType === 'updated' || actionType === 'deleted'
            ? {
                actor: actorName,
                incidentDate,
                employeeName,
              }
            : {
                dateOfIncident: incidentDate,
                employeeName,
              },
      category: EnrichedActivityLogCategoryEnum.FirstAidLog,
    },
  };
};

export const createOptimisticMhRiskAssessmentLogEntry = ({
  actionType,
  actorName,
  surveyName,
}: {
  actionType: MhOptimisticActionType;
  actorName: string;
  surveyName: string;
}): EnrichedActivityLog | null => {
  if (!surveyName) return null;
  return {
    id: `optimistic-mh-${actionType}-${Date.now()}`,
    timestamp: new Date().toISOString(),
    translationKeyLabel: `activity_log.mh_risk_assessment.${actionType}.label`,
    translationKeyTitle: `activity_log.mh_risk_assessment.${actionType}.title`,
    translationParams: {
      actor: actorName,
      surveyName,
    },
    category: EnrichedActivityLogCategoryEnum.MhRiskAssessment,
  };
};

export const createOptimisticMaternityNotificationLogEntry = ({
  actionType,
  actorName,
  employeeName,
  dueDate,
}: {
  actionType: MaternityNotificationOptimisticActionType;
  actorName: string;
  employeeName: string;
  dueDate: string;
}): EnrichedActivityLog | null => {
  if (!employeeName || !dueDate) return null;
  return {
    id: `optimistic-maternity-notification-${actionType}-${Date.now()}`,
    timestamp: new Date().toISOString(),
    translationKeyLabel: `activity_log.maternity_notification.${actionType}.label`,
    translationKeyTitle: `activity_log.maternity_notification.${actionType}.title`,
    translationParams: {
      actor: actorName,
      employeeName,
      dueDate: formatDateToDe(dueDate),
    },
    category: EnrichedActivityLogCategoryEnum.Maternity,
  };
};

export const createOptimisticPreventionLogPayload = ({
  actionType,
  assignment,
  actorName,
  completionDate,
}: {
  actionType: PreventionOptimisticActionType;
  assignment: PreventionAssignment | null | undefined;
  actorName: string;
  completionDate?: string;
}): { employeeId: string; entry: EnrichedActivityLog } | null => {
  if (
    !assignment?.employee?.id ||
    !assignment.preventionTrigger?.name ||
    !assignment.preventionProfile?.category
  ) {
    return null;
  }

  const categoryKey = assignment.preventionProfile.category.toLowerCase();
  const employeeName = printEntityName(assignment.employee);
  const preventionName = assignment.preventionTrigger.name;
  const dueDate = formatDateToDe(assignment.deadline);
  const formattedCompletionDate = completionDate ? formatDateToDe(completionDate) : '';

  if (actionType === 'deadline_updated') {
    return {
      employeeId: assignment.employee.id,
      entry: {
        id: `optimistic-prevention-deadline-${Date.now()}`,
        timestamp: new Date().toISOString(),
        translationKeyLabel: `activity_log.prevention_deadline.update.${categoryKey}.label`,
        translationKeyTitle: `activity_log.prevention_deadline.update.${categoryKey}.title`,
        translationParams: {
          triggerName: preventionName,
          firstName: assignment.employee.firstName ?? '',
          lastName: assignment.employee.lastName ?? '',
          date: dueDate,
        },
        category: EnrichedActivityLogCategoryEnum.PreventionRegister,
      },
    };
  }

  if (!formattedCompletionDate) return null;

  const keyPrefix =
    actionType === 'completion_created'
      ? 'activity_log.prevention_completion.created'
      : actionType === 'completion_updated'
        ? 'activity_log.prevention_completion.updated'
        : actionType === 'receipt_uploaded'
          ? 'activity_log.prevention_receipt.uploaded'
          : 'activity_log.prevention_receipt.deleted';

  return {
    employeeId: assignment.employee.id,
    entry: {
      id: `optimistic-prevention-${actionType}-${Date.now()}`,
      timestamp: new Date().toISOString(),
      translationKeyLabel: `${keyPrefix}.${categoryKey}.label`,
      translationKeyTitle: `${keyPrefix}.${categoryKey}.title`,
      translationParams:
        actionType === 'completion_updated'
          ? {
              preventionName,
              employeeName,
              dateOfCompletion: formattedCompletionDate,
            }
          : {
              preventionName,
              employeeName,
              dateOfCompletion: formattedCompletionDate,
              actor: actorName,
            },
      category: EnrichedActivityLogCategoryEnum.PreventionRegister,
    },
  };
};

export const createOptimisticInstructionAssignmentLogPayload = ({
  employee,
  actionType,
  translationParams,
}: {
  employee: Employee;
  actionType: InstructionAssignmentOptimisticActionType;
  translationParams: InstructionAssignmentOptimisticTranslationParams;
}): { employeeId: string; entry: EnrichedActivityLog } | null => {
  if (!employee?.id) return null;

  return {
    employeeId: employee.id,
    entry: {
      id: `optimistic-instruction-${actionType}-${Date.now()}`,
      timestamp: new Date().toISOString(),
      translationKeyLabel: `activity_log.instruction_assignment.${actionType}.label`,
      translationKeyTitle: `activity_log.instruction_assignment.${actionType}.title`,
      translationParams,
      category: EnrichedActivityLogCategoryEnum.Instructions,
    },
  };
};
